import { useEffect, useState, type FormEvent } from "react";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/Restore";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listOrganizations } from "@/api/organizations";
import {
  type TenantOut,
  type TenantPlan,
  createTenant,
  listTenants,
  updateTenant,
} from "@/api/tenants";
import type { Organization } from "@/api/types";
import { OrganizationFormDrawer } from "@/components/OrganizationFormDrawer";
import { PageHeader } from "@/components/PageHeader";
import { useAuthStore } from "@/store/authStore";

const STATUS_COLOR: Record<
  string,
  "default" | "primary" | "success" | "warning" | "error" | "info"
> = {
  ACTIVE: "success",
  SUSPENDED: "warning",
  ARCHIVED: "default",
};

const PLAN_COLOR: Record<string, "primary" | "secondary" | "default"> = {
  FREE: "default",
  PRO: "primary",
  ENTERPRISE: "secondary",
};

export function TenantsAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);
  const isGod = Boolean(me?.is_god);

  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TenantOut | null>(null);
  const [orgsFor, setOrgsFor] = useState<TenantOut | null>(null);

  const tenants = useQuery({
    queryKey: ["tenants", q],
    queryFn: () => listTenants({ q: q || undefined, limit: 200 }),
    enabled: isGod,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "ACTIVE" | "SUSPENDED" | "ARCHIVED" }) =>
      updateTenant(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });

  if (!isGod) {
    return (
      <Box>
        <PageHeader title={t("tenants.title")} subtitle={t("tenants.subtitle")} helpTerm="tenant" />
        <Alert severity="warning">{t("tenants.forbidden")}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("tenants.title")}
        subtitle={t("tenants.subtitle")}
        actions={
          <>
            <IconButton onClick={() => tenants.refetch()} aria-label={t("common.retry")}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              {t("tenants.createCta")}
            </Button>
          </>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label={t("common.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 280 }}
        />
      </Stack>

      {tenants.isLoading ? (
        <CircularProgress />
      ) : (tenants.data?.items ?? []).length === 0 ? (
        <Alert severity="info">{t("tenants.empty")}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("tenants.col.slug")}</TableCell>
                <TableCell>{t("tenants.col.name")}</TableCell>
                <TableCell>{t("tenants.col.plan")}</TableCell>
                <TableCell>{t("tenants.col.status")}</TableCell>
                <TableCell align="right">{t("tenants.col.orgs")}</TableCell>
                <TableCell align="right">{t("tenants.col.practitioners")}</TableCell>
                <TableCell align="right">{t("tenants.col.users")}</TableCell>
                <TableCell align="right">{t("tenants.col.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tenants.data?.items ?? []).map((t: TenantOut) => (
                <TenantRow
                  key={t.id}
                  tenant={t}
                  onSuspend={() => updateMutation.mutate({ id: t.id, status: "SUSPENDED" })}
                  onReactivate={() => updateMutation.mutate({ id: t.id, status: "ACTIVE" })}
                  onArchive={() => updateMutation.mutate({ id: t.id, status: "ARCHIVED" })}
                  onEdit={() => setEditing(t)}
                  onOrgs={() => setOrgsFor(t)}
                  busy={updateMutation.isPending}
                />
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          qc.invalidateQueries({ queryKey: ["tenants"] });
        }}
      />

      <EditTenantDialog
        tenant={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ["tenants"] });
        }}
      />

      <TenantOrgsDialog tenant={orgsFor} onClose={() => setOrgsFor(null)} />
    </Box>
  );
}


function TenantRow({
  tenant,
  onSuspend,
  onReactivate,
  onArchive,
  onEdit,
  onOrgs,
  busy,
}: {
  tenant: TenantOut;
  onSuspend: () => void;
  onReactivate: () => void;
  onArchive: () => void;
  onEdit: () => void;
  onOrgs: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <TableRow hover>
      <TableCell sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>
        {tenant.slug}
      </TableCell>
      <TableCell>{tenant.name}</TableCell>
      <TableCell>
        <Chip
          size="small"
          color={PLAN_COLOR[tenant.plan] ?? "default"}
          label={tenant.plan}
        />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={STATUS_COLOR[tenant.status] ?? "default"}
          label={t(`tenants.status.${tenant.status}`)}
        />
      </TableCell>
      <TableCell align="right">{tenant.org_count}</TableCell>
      <TableCell align="right">{tenant.practitioner_count}</TableCell>
      <TableCell align="right">{tenant.user_count}</TableCell>
      <TableCell align="right">
        <Tooltip title={t("tenants.manageOrgs")}>
          <span>
            <IconButton size="small" onClick={onOrgs} disabled={busy}>
              <AccountTreeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t("tenants.edit")}>
          <span>
            <IconButton size="small" onClick={onEdit} disabled={busy}>
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {tenant.status === "ACTIVE" && (
          <Tooltip title={t("tenants.suspend")}>
            <span>
              <IconButton size="small" color="warning" onClick={onSuspend} disabled={busy}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {tenant.status === "SUSPENDED" && (
          <Tooltip title={t("tenants.reactivate")}>
            <span>
              <IconButton size="small" color="success" onClick={onReactivate} disabled={busy}>
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {tenant.status !== "ARCHIVED" && (
          <Tooltip title={t("tenants.archive")}>
            <span>
              <IconButton size="small" color="error" onClick={onArchive} disabled={busy}>
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}


/** Full tenant edit for a GOD, including the settings a tenant admin would
 *  reach from /me/tenant — which a GOD cannot, holding no account inside. */
function EditTenantDialog({
  tenant,
  onClose,
  onSaved,
}: {
  tenant: TenantOut | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [plan, setPlan] = useState<TenantPlan>("PRO");
  const [brandColor, setBrandColor] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [defaultLocale, setDefaultLocale] = useState("fr");
  const [auditRetentionDays, setAuditRetentionDays] = useState(730);
  const [planBlancEnabled, setPlanBlancEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      setName(tenant.name);
      setPlan(tenant.plan);
      setBrandColor(tenant.brand_color ?? "");
      setSupportEmail(tenant.support_email ?? "");
      setDefaultLocale(tenant.default_locale ?? "fr");
      setAuditRetentionDays(tenant.audit_retention_days ?? 730);
      setPlanBlancEnabled(tenant.plan_blanc_enabled ?? false);
      setError(null);
    }
  }, [tenant]);

  const mutation = useMutation({
    mutationFn: () =>
      updateTenant(tenant?.id as string, {
        name: name.trim(),
        plan,
        brand_color: brandColor.trim() || null,
        support_email: supportEmail.trim() || null,
        default_locale: defaultLocale,
        audit_retention_days: auditRetentionDays,
        plan_blanc_enabled: planBlancEnabled,
      }),
    onSuccess: () => onSaved(),
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || "error");
    },
  });

  return (
    <Dialog open={Boolean(tenant)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("tenants.editTitle")}
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
          {tenant?.slug}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error">
              {t(`tenants.err.${error}`, { defaultValue: error })}
            </Alert>
          )}
          <TextField
            label={t("tenants.field.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <FormControl size="small" fullWidth>
            <InputLabel>{t("tenants.field.plan")}</InputLabel>
            <Select
              label={t("tenants.field.plan")}
              value={plan}
              onChange={(e) => setPlan(e.target.value as TenantPlan)}
            >
              <MenuItem value="FREE">FREE</MenuItem>
              <MenuItem value="PRO">PRO</MenuItem>
              <MenuItem value="ENTERPRISE">ENTERPRISE</MenuItem>
            </Select>
          </FormControl>

          <Divider />
          <Typography variant="overline" color="text.secondary">
            {t("tenantSettings.title")}
          </Typography>
          <TextField
            label={t("tenantSettings.brandColor")}
            value={brandColor}
            onChange={(e) => setBrandColor(e.target.value)}
            placeholder="#2563FF"
            size="small"
            fullWidth
            helperText={t("tenantSettings.brandColorHelp")}
          />
          <TextField
            label={t("tenantSettings.supportEmail")}
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            type="email"
            size="small"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>{t("tenantSettings.defaultLocale")}</InputLabel>
            <Select
              label={t("tenantSettings.defaultLocale")}
              value={defaultLocale}
              onChange={(e) => setDefaultLocale(e.target.value)}
            >
              <MenuItem value="fr">fr</MenuItem>
              <MenuItem value="en">en</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t("tenantSettings.auditRetentionDays")}
            type="number"
            size="small"
            fullWidth
            value={auditRetentionDays}
            onChange={(e) => setAuditRetentionDays(Number(e.target.value) || 0)}
            inputProps={{ min: 30, max: 3650 }}
            helperText={t("tenantSettings.auditRetentionHelp")}
          />
          <FormControlLabel
            control={
              <Switch
                checked={planBlancEnabled}
                onChange={(e) => setPlanBlancEnabled(e.target.checked)}
              />
            }
            label={t("tenantSettings.planBlancEnabled")}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            {t("tenantSettings.planBlancEnabledHelp")}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !name.trim()}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


/** The tenant's organisations — what "sub-tenants" map to in this model. */
function TenantOrgsDialog({
  tenant,
  onClose,
}: {
  tenant: TenantOut | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);

  const orgs = useQuery({
    queryKey: ["tenant-orgs", tenant?.id],
    queryFn: () => listOrganizations({ tenant_id: tenant?.id, limit: 200 }),
    enabled: Boolean(tenant),
  });

  const byId = new Map((orgs.data?.items ?? []).map((o) => [o.id, o]));
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["tenant-orgs", tenant?.id] });
    qc.invalidateQueries({ queryKey: ["organizations"] });
    qc.invalidateQueries({ queryKey: ["tenants"] });
  };

  return (
    <>
      <Dialog open={Boolean(tenant) && !formOpen} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          {t("tenants.orgsTitle")}
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {tenant?.slug}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {orgs.isLoading ? (
            <CircularProgress size={24} />
          ) : (orgs.data?.items ?? []).length === 0 ? (
            <Alert severity="info">{t("tenants.orgsEmpty")}</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("orgs.code")}</TableCell>
                  <TableCell>{t("orgs.name")}</TableCell>
                  <TableCell>{t("orgs.kind")}</TableCell>
                  <TableCell>{t("orgs.parent")}</TableCell>
                  <TableCell align="right">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(orgs.data?.items ?? []).map((o) => (
                  <TableRow key={o.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
                      {o.code}
                    </TableCell>
                    <TableCell>{o.name}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={o.kind} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
                      {o.parent_id ? (byId.get(o.parent_id)?.code ?? "—") : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingOrg(o);
                          setFormOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingOrg(null);
              setFormOpen(true);
            }}
          >
            {t("tenants.orgsAdd")}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>

      <OrganizationFormDrawer
        open={formOpen}
        organization={editingOrg}
        tenantId={tenant?.id}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          refresh();
        }}
      />
    </>
  );
}


function CreateTenantDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [plan, setPlan] = useState<TenantPlan>("PRO");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminFullName, setAdminFullName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createTenant({
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        plan,
        admin_email: adminEmail.trim().toLowerCase() || null,
        admin_full_name: adminFullName.trim() || null,
      }),
    onSuccess: () => {
      setName("");
      setSlug("");
      setPlan("PRO");
      setAdminEmail("");
      setAdminFullName("");
      setError(null);
      onCreated();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response
        ?.data?.detail;
      setError(detail || "error");
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !slug.trim()) {
      setError("required_fields_missing");
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("tenants.createTitle")}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error">
                {t(`tenants.err.${error}`, { defaultValue: error })}
              </Alert>
            )}
            <TextField
              label={t("tenants.field.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label={t("tenants.field.slug")}
              value={slug}
              onChange={(e) =>
                setSlug(e.target.value.replace(/\s+/g, "-").toLowerCase())
              }
              required
              fullWidth
              size="small"
              helperText={t("tenants.field.slugHint")}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t("tenants.field.plan")}</InputLabel>
              <Select
                label={t("tenants.field.plan")}
                value={plan}
                onChange={(e) => setPlan(e.target.value as TenantPlan)}
              >
                <MenuItem value="FREE">FREE</MenuItem>
                <MenuItem value="PRO">PRO</MenuItem>
                <MenuItem value="ENTERPRISE">ENTERPRISE</MenuItem>
              </Select>
            </FormControl>

            <Divider />
            <Typography variant="overline" color="text.secondary">
              {t("tenants.field.adminSection")}
            </Typography>
            <TextField
              label={t("tenants.field.adminEmail")}
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              fullWidth
              size="small"
              autoComplete="off"
              helperText={t("tenants.field.adminEmailHint")}
            />
            <TextField
              label={t("tenants.field.adminFullName")}
              value={adminFullName}
              onChange={(e) => setAdminFullName(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {t("tenants.createCta")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
